import { FormSwitch } from '@ipfabric/ui-components/FormSwitch/FormSwitch';
import { FormTextField } from '@ipfabric/ui-components/FormTextField/FormTextField';
import type { FormSnippet } from '@ipfabric/ui-components/utils/formSnippets/FormSnippet';
import { hookFormZod } from '@ipfabric/ui-components/utils/hookForm/hookFormZod';
import { type UseFormReturn, useWatch } from 'react-hook-form';
import { z } from 'zod';
import {
  FULL_BGP_LIMIT_MAX,
  FULL_BGP_LIMIT_MIN,
} from '../../../../../../../packages/api-schemas/src/lib/settings/discovery/fullBgpLimit';
import type { DiscoverySettingsObject } from '../../model/DiscoverySettingsObject';

type FullBgpIpv4LimitFieldValues = {
  fullBgpIpv4LimitEnabled: boolean;
  fullBgpIpv4Limit: string;
};
type FullBgpIpv4LimitOutputValues = {
  fullBgpIpv4LimitEnabled: boolean;
  fullBgpIpv4Limit: number;
};

const FullBgpIpv4LimitField = (props: {
  form: UseFormReturn<
    FullBgpIpv4LimitFieldValues,
    unknown,
    FullBgpIpv4LimitOutputValues
  >;
}) => {
  const fullBgpIpv4LimitEnabled = useWatch({
    control: props.form.control,
    name: 'fullBgpIpv4LimitEnabled',
  });
  return (
    <div className='flex flex-col gap-3xs'>
      <FormSwitch
        control={props.form.control}
        name='fullBgpIpv4LimitEnabled'
        label='Use threshold limit for IPv4'
      />
      <div className='w-60 ml-12'>
        <FormTextField
          control={props.form.control}
          type='number'
          name='fullBgpIpv4Limit'
          disabled={!fullBgpIpv4LimitEnabled}
        />
      </div>
    </div>
  );
};

export const fullBgpIpv4Limit: FormSnippet<
  DiscoverySettingsObject,
  FullBgpIpv4LimitFieldValues,
  FullBgpIpv4LimitOutputValues
> = {
  render: ({ form }) => <FullBgpIpv4LimitField form={form} />,
  fromModel: (model, fieldValuesDraft) => {
    fieldValuesDraft.fullBgpIpv4LimitEnabled =
      model.fullBgpLimit?.enabled ?? false;
    fieldValuesDraft.fullBgpIpv4Limit =
      model.fullBgpLimit?.threshold?.toString() ?? '';
  },
  toModel: (transformedValues, modelDraft) => {
    modelDraft.fullBgpLimit = {
      enabled: transformedValues.fullBgpIpv4LimitEnabled,
      threshold: transformedValues.fullBgpIpv4Limit,
    };
  },
  schema: z.object({
    fullBgpIpv4LimitEnabled: z.boolean(),
    fullBgpIpv4Limit: hookFormZod.numberInputWith(
      hookFormZod.minMax(FULL_BGP_LIMIT_MIN, FULL_BGP_LIMIT_MAX),
    ),
  }),
};
